'use client';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ophelos/ui/components/card';
import { Separator } from '@ophelos/ui/components/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ophelos/ui/components/alert-dialog';
import { BanknoteX, CheckCircle2, Copy, Lightbulb } from 'lucide-react';
import { Trans } from 'react-i18next';
import { PlanBalanceChart } from '@/components/payments/PlanBalanceChart';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { PaymentPlanProgress } from '@/components/ui/PaymentPlanProgress';
import { useTranslation } from '@/hooks/useTranslation';
import { toast } from 'sonner';
import { usePaymentApi, PaymentApiError } from '@/lib/payment-api';
import { formatMoneyInLanguage } from '@/lib/currency';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import type { OphelosPaymentPlanResponse } from '@/types/payment-plan';
import { SCHEDULING_MIN_LEAD_DAYS, type PaymentMethod } from '@ophelos/shared/constants/payment-methods';
import { isWithinLeadDays } from '@/lib/payment-schedule';

interface PaymentPlanData {
  frequency: string;
  startedOn: string;
  paymentAmount: number;
  nextPaymentDate: string;
  completedPayments: number;
  totalPayments: number;
  totalPaid: number;
  currentBalance: number;
  originalBalance: number;
}

interface ChartDataPoint {
  month: string;
  balance?: number;
  predicted?: number;
}

interface PaymentPlanOverviewProps {
  paymentPlanData: PaymentPlanData;
  chartData: ChartDataPoint[];
  paymentPlanId?: string; // External payment plan ID (pln_xxx)
  rawPlanData?: OphelosPaymentPlanResponse; // Full Ophelos plan for installment button
}

export function PaymentPlanOverview({ paymentPlanData, chartData, paymentPlanId, rawPlanData }: PaymentPlanOverviewProps) {
  const { t, currentLanguage } = useTranslation();
  const currency = rawPlanData?.currency ?? 'EUR';
  const navigate = useNavigate();
  const { isPreviewMode } = useConfiguration();
  const { cancelPaymentPlan } = usePaymentApi();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [copiedEntity, setCopiedEntity] = useState(false);
  const [copiedReference, setCopiedReference] = useState(false);

  const copyToClipboard = async (text: string, setCopied: (value: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const MANUAL_PLAN_METHODS = ['multibanco', 'mb_way'];
  const isManualPlan =
    !!rawPlanData?.payment_method && MANUAL_PLAN_METHODS.includes(rawPlanData.payment_method);

  const nextUnpaidInstallment = rawPlanData?.schedule.find((item) => item.status !== 'succeeded');

  // Bacs (and any future lead-time-gated method) creates its payment intent
  // days before the due date — cancelling the plan can no longer stop a
  // charge that's already within that window.
  const leadDays = rawPlanData?.payment_method
    ? SCHEDULING_MIN_LEAD_DAYS[rawPlanData.payment_method as PaymentMethod]
    : undefined;
  const chargeAlreadyInFlight =
    leadDays !== undefined &&
    (nextUnpaidInstallment?.status === 'pending' || nextUnpaidInstallment?.status === 'processing') &&
    isWithinLeadDays(nextUnpaidInstallment.date, leadDays);

  const today = new Date().toLocaleDateString('en-CA');
  const installmentIsDueToday = nextUnpaidInstallment
    ? nextUnpaidInstallment.date === today
    : false;
  const installmentIsOverdue = nextUnpaidInstallment
    ? nextUnpaidInstallment.date < today
    : false;

  const installmentAmount = nextUnpaidInstallment ? nextUnpaidInstallment.subunits / 100 : 0;
  const installmentAmountFormatted = formatMoneyInLanguage(installmentAmount, currentLanguage, currency);

  // Keyed on lowercase so both the capitalized display strings from PaymentPlan
  // and the raw lowercase domain values (weekly/biweekly/monthly) resolve.
  const frequencyLabelMap: Record<string, string> = {
    weekly: t('paymentPlan.weekly'),
    biweekly: t('paymentPlan.biweekly'),
    'bi-weekly': t('paymentPlan.biweekly'),
    monthly: t('paymentPlan.monthly'),
    custom: t('paymentPlan.custom'),
  };
  const frequencyLabel = frequencyLabelMap[paymentPlanData.frequency.toLowerCase()] ?? paymentPlanData.frequency;

  const handleCancelPaymentPlan = async () => {
    if (!paymentPlanId) {
      toast.error(t('paymentPlan.planIdNotFound'));
      setShowCancelDialog(false);
      return;
    }

    setIsCancelling(true);

    try {
      await cancelPaymentPlan(paymentPlanId);
      toast.success(t('paymentPlan.cancelSuccess'));
      setShowCancelDialog(false);

      // Navigate back to dashboard and signal the cancellation so it can refresh
      // its cache and show a persistent banner (the toast alone is easy to miss
      // during the page transition).
      navigate(isPreviewMode ? '/dashboard?preview=true&plan_cancelled=true' : '/dashboard?plan_cancelled=true');
    } catch (error) {
      console.error('Failed to cancel payment plan:', error);

      if (error instanceof PaymentApiError) {
        toast.error(error.message);
      } else {
        toast.error(t('paymentPlan.cancelFailed'));
      }
    } finally {
      setIsCancelling(false);
    }
  };


  return (
    <div className="flex-1 overflow-y-auto min-h-0 px-4">
      <div className="xs:max-w-lg w-full mx-auto flex flex-col gap-6 ">
        {/* Progress Chart */}
        <Card variant="fullscreen" className="gap-5">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('paymentPlan.remainingBalance')}</p>
                <div className="text-3xl font-bold">
                  <CurrencyDisplay amount={paymentPlanData.currentBalance} currency={currency} className="text-3xl" centsClassName="text-xl" />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Progress Bar - matching dashboard style */}
              <PaymentPlanProgress
                totalPayments={paymentPlanData.totalPayments}
                completedPayments={paymentPlanData.completedPayments}
                currency={currency}
              />

              {/* Chart - Staircase Line Chart */}
              <div className="h-48 mt-10">
                <PlanBalanceChart data={chartData} currency={currency} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Details */}
        <Card variant="fullscreen" className="gap-5">
          <CardHeader>
            <CardTitle>{t('paymentPlan.planDetails')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('paymentPlan.originalBalance')}</span>
                <span className="font-medium">
                  <CurrencyDisplay amount={paymentPlanData.originalBalance} currency={currency} />
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('paymentPlan.amountPaid')}</span>
                <span className="font-medium">
                  <CurrencyDisplay amount={paymentPlanData.totalPaid} currency={currency} />
                </span>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('paymentPlan.startedOn')}</span>
                <span className="text-sm font-medium">
                  {new Date(paymentPlanData.startedOn).toLocaleDateString(currentLanguage, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('paymentPlan.frequency')}</span>
                <span className="text-sm font-medium">{frequencyLabel}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('paymentPlan.paymentAmount')}</span>
                <span className="text-sm font-semibold">
                  <CurrencyDisplay amount={paymentPlanData.paymentAmount} currency={currency} />
                </span>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('paymentPlan.nextPayment')}</span>
                <span className="text-sm font-medium">
                  {new Date(paymentPlanData.nextPaymentDate).toLocaleDateString(currentLanguage, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Multibanco reference — manual plans only (always visible, with due-date context).
            Gated on the live status as an allow-list, the inverse of the cancel card below:
            handing someone an ATM reference for a plan Ophelos has already closed takes a
            payment that no longer settles anything, so this one fails closed. */}
        {isManualPlan && nextUnpaidInstallment && rawPlanData?.status === 'active' && (
          <Card variant="fullscreen" className="gap-5">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{(installmentIsDueToday || installmentIsOverdue) ? t('paymentPlan.paymentDueToday') : t('paymentPlan.upcomingInstallment')}</CardTitle>
                <span className="text-sm font-semibold">
                  <CurrencyDisplay amount={installmentAmount} currency={currency} />
                </span>
              </div>
              {!(installmentIsDueToday || installmentIsOverdue) && (
                <p className="text-xs text-muted-foreground">
                  {t('paymentPlan.dueOn', { date: new Date(nextUnpaidInstallment.date).toLocaleDateString(currentLanguage, { month: 'short', day: 'numeric', year: 'numeric' }) })}
                </p>
              )}
            </CardHeader>
            <CardContent>
              {rawPlanData?.multibanco_entity && rawPlanData?.multibanco_reference ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">{t('multibanco.subtitle')}</p>

                  <div className="stack gap-2">
                    <div className="text-sm font-medium">{t('multibanco.entity')}</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 rounded-xl bg-soft shadow-xs border border-border-light p-3">
                        <div className="text-2xl font-mono font-bold tracking-wider">{rawPlanData.multibanco_entity}</div>
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(rawPlanData.multibanco_entity!, setCopiedEntity)}
                        title={t('multibanco.copyEntity')}
                      >
                        {copiedEntity ? <CheckCircle2 className="size-4 text-green-600" /> : <Copy className="size-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="stack gap-2">
                    <div className="text-sm font-medium">{t('multibanco.reference')}</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 rounded-xl bg-soft shadow-xs border border-border-light p-3">
                        <div className="text-2xl font-mono font-bold tracking-wider">{rawPlanData.multibanco_reference}</div>
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(rawPlanData.multibanco_reference!, setCopiedReference)}
                        title={t('multibanco.copyReference')}
                      >
                        {copiedReference ? <CheckCircle2 className="size-4 text-green-600" /> : <Copy className="size-4" />}
                      </Button>
                    </div>
                  </div>

                  <Alert variant="warning">
                    <Lightbulb />
                    <AlertTitle>{t('multibanco.howToPay')}</AlertTitle>
                    <AlertDescription>
                      <ol className="list-decimal space-y-1 pl-4 mt-1">
                        <li>{t('multibanco.step1')}</li>
                        <li>{t('multibanco.step2')}</li>
                        <li>
                          <Trans i18nKey="multibanco.step3Entity" values={{ entity: rawPlanData.multibanco_entity }} components={{ strong: <strong /> }} />
                        </li>
                        <li>
                          <Trans i18nKey="multibanco.step3Reference" values={{ reference: rawPlanData.multibanco_reference }} components={{ strong: <strong /> }} />
                        </li>
                        <li>
                          <Trans i18nKey="multibanco.step3Amount" values={{ amount: installmentAmountFormatted }} components={{ strong: <strong /> }} />
                        </li>
                        <li>{t('multibanco.step6')}</li>
                      </ol>
                      <p className="mt-3">{t('multibanco.onlineBankingNote')}</p>
                    </AlertDescription>
                  </Alert>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t('paymentPlan.preparingPayment')}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Connect selects the plan on the local payment_plans row, whose status lags the live
            Ophelos one it then returns — so a plan Ophelos already considers finished or
            cancelled still reaches this page, and without this guard would offer to cancel
            itself. rawPlanData is fetched once per page load, so this covers a plan that had
            already finished when the page opened, not one that finishes while it is open. */}
        {rawPlanData?.status !== 'cancelled' && rawPlanData?.status !== 'succeeded' && (
          <Card variant="fullscreen" className="gap-5 pb-2">
            <CardHeader>
              <CardTitle>{t('paymentPlan.cancelPlanTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-0 px-2">
              <Button
                variant="ghost"
                className="w-full justify-start h-auto py-3 px-4 rounded-lg! hover:bg-soft/50 text-destructive hover:text-destructive"
                onClick={() => setShowCancelDialog(true)}
              >
                <div className="flex items-center gap-3">
                  <div className="size-10 bg-soft rounded-full flex items-center justify-center shrink-0">
                    <BanknoteX className="size-4" />
                  </div>
                  <span className="text-sm font-medium">{t('paymentPlan.cancelPlan')}</span>
                </div>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Cancel Payment Plan Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('paymentPlan.cancelDialogTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('paymentPlan.cancelDialogDescription')}
              {chargeAlreadyInFlight && <> {t('paymentPlan.cancelDialogInFlightWarning')}</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling}>{t('paymentPlan.keepPlan')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelPaymentPlan} disabled={isCancelling} variant="destructive">
              {isCancelling ? t('paymentPlan.cancelling') : t('paymentPlan.cancelPlanAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
